import React, { useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaGraduationCap,
  FaLightbulb,
  FaMobileAlt,
  FaQuestionCircle,
  FaSearch,
  FaStar,
} from "react-icons/fa";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { HELP_CATEGORIES, TEACHER_HELP_ITEMS } from "./teacherHelpContent";

const QUICK_SEARCHES = [
  "présence",
  "compétition",
  "palmarès",
  "prépa physique",
  "catégories",
  "adhérent",
  "objectifs",
];

const normalizeText = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const buildSearchableText = (item) =>
  normalizeText(
    [
      item.title,
      item.category,
      item.shortDescription,
      ...(item.searchTags || []),
      ...(item.steps || []),
      ...(item.quickActions || []),
      ...(item.tips || []),
      ...(item.mobileTips || []),
    ].join(" "),
  );

const getItemScore = (item, tokens) => {
  if (!tokens.length) return 1;

  const title = normalizeText(item.title);
  const category = normalizeText(item.category);
  const shortDescription = normalizeText(item.shortDescription);
  const keywords = normalizeText((item.searchTags || []).join(" "));
  const searchable = buildSearchableText(item);

  let score = 0;

  tokens.forEach((token) => {
    if (title.includes(token)) score += 8;
    if (category.includes(token)) score += 5;
    if (keywords.includes(token)) score += 6;
    if (shortDescription.includes(token)) score += 4;
    if (searchable.includes(token)) score += 2;
  });

  return score;
};

function TeacherHelpHeader() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="relative overflow-hidden bg-gradient-to-r from-[#013E8E] via-indigo-600 to-blue-600 px-5 py-7 text-white sm:px-7 lg:px-8">
        <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-[#4CA82F]/20 blur-2xl" />

        <div className="relative flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] ring-1 ring-white/20">
              <FaGraduationCap />
              Aide enseignants
            </div>

            <h1 className="mt-4 text-2xl font-bold sm:text-3xl">
              Mini tutoriels du Dashboard Enseignant
            </h1>

            <p className="mt-3 max-w-3xl text-sm leading-6 text-white/90 sm:text-base">
              Retrouvez rapidement comment utiliser les principales pages :
              adhérents, appels, tableau de présence, compétitions, préparation
              physique, grades, objectifs et ressources pédagogiques.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:min-w-[360px]">
            <HeaderStat label="Pages" value={TEACHER_HELP_ITEMS.length} />
            <HeaderStat label="Catégories" value={HELP_CATEGORIES.length} />
            <HeaderStat
              label="Guides"
              value={TEACHER_HELP_ITEMS.reduce(
                (sum, item) => sum + (item.steps?.length || 0),
                0,
              )}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeaderStat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white/15 px-4 py-3 text-white ring-1 ring-white/20 backdrop-blur">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/70">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.16em] text-slate-700 dark:text-slate-200">
      {children}
    </h4>
  );
}

function BulletList({ items = [], icon: Icon, iconClassName = "" }) {
  return (
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li
          key={`${item}-${index}`}
          className="flex items-start gap-2 text-sm leading-6 text-slate-700 dark:text-slate-300"
        >
          {Icon ? (
            <Icon className={`mt-1 shrink-0 ${iconClassName}`} />
          ) : (
            <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400" />
          )}

          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function SearchAndFilters({
  search,
  setSearch,
  activeCategory,
  setActiveCategory,
  resultCount,
}) {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Recherche intelligente
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Trouver rapidement une aide
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Recherchez par page, action, mot-clé ou besoin terrain : présence,
              compétition, catégorie, palmarès, prépa physique, adhérent...
            </p>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
            <span className="text-slate-500 dark:text-slate-400">
              Résultats :
            </span>{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {resultCount}
            </span>
          </div>
        </div>

        <div className="mt-5">
          <label className="mb-2 block text-sm font-bold text-slate-800 dark:text-slate-100">
            Recherche
          </label>

          <div className="relative">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Ex : appel, tableau, compétition, résultats, catégorie, groupe..."
              className="w-full rounded-2xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:ring-indigo-900/30"
            />
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {QUICK_SEARCHES.map((term) => (
              <button
                key={term}
                type="button"
                onClick={() => setSearch(term)}
                className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-700"
              >
                {term}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <p className="mb-2 text-sm font-bold text-slate-800 dark:text-slate-100">
            Catégories
          </p>

          <div className="flex flex-wrap gap-2">
            {["Toutes", ...HELP_CATEGORIES].map((category) => {
              const active = activeCategory === category;

              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`rounded-full px-4 py-2 text-sm font-bold transition ${
                    active
                      ? "bg-[#013E8E] text-white ring-2 ring-indigo-200 dark:ring-indigo-800/50"
                      : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800"
                  }`}
                >
                  {category}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedHelpCard({ item, openId, setOpenId }) {
  if (!item) return null;

  const isOpen = openId === item.id;

  return (
    <section className="overflow-hidden rounded-[28px] border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 shadow-sm dark:border-amber-800/50 dark:from-amber-900/20 dark:to-orange-900/20">
      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
              <FaLightbulb />
              Conseil du jour
            </div>

            <h3 className="mt-3 text-xl font-bold text-slate-900 dark:text-white">
              {item.title}
            </h3>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700 dark:text-slate-300">
              {item.shortDescription}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setOpenId(isOpen ? null : item.id)}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-slate-900 ring-1 ring-amber-200 transition hover:bg-amber-50 dark:bg-slate-900 dark:text-white dark:ring-slate-700 dark:hover:bg-slate-800"
          >
            {isOpen ? <FaChevronUp /> : <FaChevronDown />}
            {isOpen ? "Replier" : "Voir l’aide"}
          </button>
        </div>
      </div>
    </section>
  );
}

function HelpItemCard({ item, isOpen, onToggle }) {
  const Icon = item.icon;

  return (
    <article className="overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-sm transition hover:shadow-md dark:border-slate-800 dark:bg-slate-900">
      <div className={`h-1.5 bg-gradient-to-r ${item.color}`} />

      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-4 p-4 text-left sm:p-5"
      >
        <div
          className={`grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br ${item.color} text-white shadow-sm`}
        >
          {Icon ? <Icon className="text-xl" /> : <FaQuestionCircle />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
              {item.category}
            </span>
          </div>

          <h3 className="mt-2 text-base font-bold text-slate-900 dark:text-white sm:text-lg">
            {item.title}
          </h3>

          <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-400">
            {item.shortDescription}
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            {(item.searchTags || []).slice(0, 5).map((tag) => (
              <span
                key={tag}
                className="rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-bold text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/50"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="shrink-0 rounded-2xl bg-slate-100 p-3 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
          {isOpen ? <FaChevronUp /> : <FaChevronDown />}
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-slate-200 px-4 py-5 dark:border-slate-800 sm:px-5">
          <div className="grid gap-5 lg:grid-cols-2">
            <div className="space-y-5">
              <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/40 dark:ring-slate-700">
                <SectionTitle>Mini tutoriel</SectionTitle>

                <ol className="space-y-2">
                  {(item.steps || []).map((step, index) => (
                    <li
                      key={`${step}-${index}`}
                      className="flex gap-3 text-sm leading-6 text-slate-700 dark:text-slate-300"
                    >
                      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#013E8E] text-xs font-bold text-white">
                        {index + 1}
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/40 dark:ring-slate-700">
                <SectionTitle>Actions rapides</SectionTitle>

                <BulletList
                  items={item.quickActions}
                  icon={FaCheckCircle}
                  iconClassName="text-emerald-500"
                />
              </div>
            </div>

            <div className="space-y-5">
              <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/40 dark:ring-slate-700">
                <SectionTitle>Conseils</SectionTitle>

                <BulletList
                  items={item.tips}
                  icon={FaLightbulb}
                  iconClassName="text-amber-500"
                />
              </div>

              <div className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200 dark:bg-slate-800/40 dark:ring-slate-700">
                <SectionTitle>Astuces smartphone</SectionTitle>

                <BulletList
                  items={item.mobileTips}
                  icon={FaMobileAlt}
                  iconClassName="text-indigo-500"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
}

export default function TeacherHelpPage() {
  useScrollToTop();

  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("Toutes");
  const [openId, setOpenId] = useState(null);

  const featuredItem = useMemo(() => {
    const index = new Date().getDate() % TEACHER_HELP_ITEMS.length;
    return TEACHER_HELP_ITEMS[index];
  }, []);

  const filteredItems = useMemo(() => {
    const tokens = normalizeText(search)
      .split(/\s+/)
      .map((token) => token.trim())
      .filter(Boolean);

    return TEACHER_HELP_ITEMS.map((item) => ({
      ...item,
      score: getItemScore(item, tokens),
    }))
      .filter((item) => {
        const categoryOk =
          activeCategory === "Toutes" || item.category === activeCategory;

        const searchOk = tokens.length === 0 ? true : item.score > 0;

        return categoryOk && searchOk;
      })
      .sort(
        (a, b) => b.score - a.score || a.title.localeCompare(b.title, "fr"),
      );
  }, [search, activeCategory]);

  const groupedItems = useMemo(() => {
    return filteredItems.reduce((acc, item) => {
      const key = item.category || "Autres";

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(item);

      return acc;
    }, {});
  }, [filteredItems]);

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <TeacherHelpHeader />

        <SearchAndFilters
          search={search}
          setSearch={setSearch}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          resultCount={filteredItems.length}
        />

        {!search.trim() && activeCategory === "Toutes" && (
          <FeaturedHelpCard
            item={featuredItem}
            openId={openId}
            setOpenId={setOpenId}
          />
        )}

        <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Guides enseignants
                </p>

                <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                  Aides disponibles
                </h2>

                <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                  Chaque aide contient un mini tutoriel, les actions rapides,
                  les conseils d’utilisation et les astuces pour smartphone.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                <span className="text-slate-500 dark:text-slate-400">
                  Total :
                </span>{" "}
                <span className="font-bold text-slate-900 dark:text-white">
                  {filteredItems.length}
                </span>
              </div>
            </div>

            <div className="mt-5">
              {filteredItems.length === 0 ? (
                <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-10 text-center dark:border-slate-700 dark:bg-slate-800/40">
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-white text-slate-400 shadow-sm dark:bg-slate-900">
                    <FaSearch />
                  </div>

                  <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
                    Aucune aide trouvée
                  </h3>

                  <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-600 dark:text-slate-400">
                    Essayez un autre mot-clé comme présence, compétition,
                    adhérent, palmarès, prépa physique ou catégorie.
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {Object.entries(groupedItems).map(([category, items]) => (
                    <section key={category}>
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                            {category}
                          </h3>

                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {items.length} aide{items.length > 1 ? "s" : ""}{" "}
                            disponible{items.length > 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {items.map((item) => (
                          <HelpItemCard
                            key={item.id}
                            item={item}
                            isOpen={openId === item.id}
                            onToggle={() =>
                              setOpenId((previous) =>
                                previous === item.id ? null : item.id,
                              )
                            }
                          />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </section>
  );
}
